import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Api } from './api.service';
import { AuthService } from './auth.service';

type availableSalesChannels = 'eCommerce';

export interface OnboardingLegalEntityData {
    id?: string; //Exists only after entity is created.
    firstName: string;
    lastName: string;
    country: string; //Iso 2 letter
}

export interface OnboardingBusinesslineData {
    industryCode: string;
    legalEntityId: string;
    salesChannels: availableSalesChannels[];
    service: 'paymentProcessing'; //Only valid option for this as
    webData: string[]; //Array of webadresses where the organization will take payment
}

export interface OnboardingStoreData {
    adress: string;
    description: string; //Reeason for the store existing (Standardize as we do today?)
    merchantId?: string; //This is up to us to set via server, not the customer.
    phoneNumber: string; //+coded
    reference?: string; //Optional store ref prefix for transaction lists.
    shopperStatement: string; //The name that will show in the consumers bank.
    splitConfiguration?: any; // Idk yet
}

export interface OnboardingAccountHolderData {
    legalEntityId: string;
    balancePlatform: string;
    description?: string;
}

export interface OnboardingBalanceAccountData {
    accountHolderId: string;
    defaultCurrencyCode: string; //3 letter ISO
    balanceAccountId?: string; //Available after creation
    description?: string;
}

export interface OnboardingHostedLinkData {
    themeId: string;
    redirectUrl: string;
    locale: string;
    settings: any;
}

export interface IFunPayOnboardingData {
    legalEntity: OnboardingLegalEntityData;
    businessLine: OnboardingBusinesslineData;
    store: OnboardingStoreData;
    accountHolder: OnboardingAccountHolderData;
    balanceAccount: OnboardingBalanceAccountData;
}

/** What the venue tells us before we can open an account on their behalf. */
export interface IFunpayOnboardingInitialData {
    shopperStatement: string;
    industryCode: string;
    phoneNumberSignatory: string;
    phoneNumberVenue: string;
    legalName: string;
    /** The premises, used as both the registered address and the store address. */
    street: string;
    city: string;
    postalCode: string;
    stateOrProvince: string;
}

export type FunpayOnboardingStepName =
    'LEGAL_ENTITY' | 'ACCOUNT_HOLDER' | 'BALANCE_ACCOUNT' | 'ONBOARDING_LINK' | 'BUSINESS_LINE' | 'LINKED_STORE' | 'COMPLETED';

export type FunpayOnboardingStatusName = 'IN_PROGRESS' | 'FAILED' | 'COMPLETED';

export interface IFunpayOnboardingState {
    step: FunpayOnboardingStepName;
    status: FunpayOnboardingStatusName;
    updatedAt: string;
    error?: { name: string; message: string } | null;
}

/** Adyen's verdict on the account, delivered by the platform webhook. */
export interface IFunpayVerification {
    accountHolderStatus?: string;
    canReceivePayments?: boolean;
    canPayOut?: boolean;
    problems?: string[];
    updatedAt?: string;
}

export interface IFunpayOnboardingIds {
    legalEntityId: string;
    accountHolderId: string;
    balanceAccountIds: string[];
    businessLineIds: string[];
    storeIds: string[];
    transferInstrumentIds: string[];
    onboardingStatus?: FunpayOnboardingStatusName;
    verification?: IFunpayVerification;
    /** Set when this venue shares another venue's FunPay account. */
    linkedFromClientId?: string;
}

/** An approved FunPay account elsewhere in the organisation that a venue can reuse. */
export interface IFunpayExistingAccount {
    clientId: string;
    clientName: string;
    legalName: string;
    canPayOut: boolean;
}

/** Store details for a venue joining an existing account. Address fields fall back to the venue's settings. */
export interface IFunpayLinkExistingData {
    sourceClientId: string;
    shopperStatement: string;
    phoneNumberVenue: string;
    street: string;
    city: string;
    postalCode: string;
    stateOrProvince?: string;
}

/** Values the server already holds, offered as the starting point for the form. */
export interface IFunpayOnboardingPrefill {
    legalName: string;
    industryCode: string;
    shopperStatement: string;
    phoneNumberVenue: string;
    phoneNumberSignatory: string;
    street: string;
    city: string;
    postalCode: string;
    stateOrProvince: string;
    country: string;
}

export interface IFunpayOnboardingStatus {
    state: IFunpayOnboardingState | null;
    ids: IFunpayOnboardingIds;
    prefill: IFunpayOnboardingPrefill;
}

/**
 * Adyen's approval plus the capability to take payments.
 */
export function isFunpayLive(verification?: Pick<IFunpayVerification, 'accountHolderStatus' | 'canReceivePayments'> | null): boolean {
    return verification?.accountHolderStatus?.toLowerCase() === 'active' && !!verification?.canReceivePayments;
}

/** Amounts are in minor units, as Adyen sends them. */
export interface IFunpayBalance {
    currency: string;
    available: number;
    balance: number;
    reserved: number;
    pending?: number;
}

export interface IFunpayBalanceAccount {
    id: string;
    description: string;
    status: string | null;
    defaultCurrencyCode: string;
    balances: IFunpayBalance[];
}

export interface IFunpayDashboard {
    verification?: IFunpayVerification;
    accountHolderId: string;
    paymentEnabled: boolean;
    paymentProvider: string | null;
    balanceAccounts: IFunpayBalanceAccount[];
}

export interface IFunpayTransaction {
    id: string;
    amount: { currency: string; value: number };
    status: 'booked' | 'pending';
    createdAt: string | null;
    bookedAt: string | null;
    valueDate: string | null;
    description: string;
    /** Adyen's transfer category, e.g. `bank` for a payout. Not always present. */
    category: string | null;
    reference: string;
}

export interface IFunpayTransactionPage {
    balanceAccountId: string;
    nextCursor: string | null;
    transactions: IFunpayTransaction[];
}

export interface IFunpayTransactionQuery {
    balanceAccountId?: string;
    from?: Date;
    to?: Date;
    cursor?: string | null;
}

/** The error code the server sent with a non-2xx response, or the fallback. */
function serverError(error: unknown, fallback: string): string {
    return error instanceof HttpErrorResponse && typeof error.error?.error === 'string' ? error.error.error : fallback;
}

@Injectable({ providedIn: 'root' })
export class FunpayHelper {
    api = inject(Api);
    auth = inject(AuthService);

    /**
     * Whether onboarding has been started for this venue, how far it got, and the values
     * the form can start from.
     */
    async getStatus(clientId: string): Promise<{ status?: IFunpayOnboardingStatus; error?: string }> {
        if (!clientId) {
            return { error: 'ONBOARDING_STATUS_FAILED_DUE_TO_MISSING_ID' };
        }

        try {
            let result = await this.api.funpay(clientId).get<IFunpayOnboardingStatus>('/status');

            if (result.success && result.data) {
                return { status: result.data };
            }

            return { error: result.error ?? 'ONBOARDING_STATUS_FAILED' };
        } catch (error) {
            console.error(error);

            return { error: 'ONBOARDING_STATUS_FAILED' };
        }
    }

    /**
     * Opens the account behind the scenes and returns Adyen's hosted onboarding link.
     * Safe to call again after a failure: steps that already succeeded are skipped, and
     * anything left out of the payload falls back to what the server stored the first time,
     * so a resume can send nothing at all.
     */
    async initOnboarding(
        clientId: string,
        onboardingData: Partial<IFunpayOnboardingInitialData> = {}
    ): Promise<{
        onboardingLink?: string;
        error?: string;
    }> {
        if (!clientId) {
            return { error: 'ONBOARDING_INIT_FAILED_DUE_TO_MISSING_ID' };
        }
        try {
            let onboarding = await this.api.funpay(clientId).post<string>('/initialise-onboarding', onboardingData);

            if (onboarding.success) {
                return {
                    onboardingLink: onboarding.data
                };
            }

            // The server names the step it failed on, so pass it through rather than flattening it.
            return {
                error: onboarding.error ?? 'ONBOARDING_INIT_FAILED'
            };
        } catch (error) {
            console.error(error);

            return {
                error: 'ONBOARDING_INIT_FAILED'
            };
        }
    }

    /** Approved accounts on other venues in the organisation that this venue may reuse. */
    async getExistingAccounts(clientId: string): Promise<{ accounts?: IFunpayExistingAccount[]; error?: string }> {
        try {
            const result = await this.api.funpay(clientId).get<IFunpayExistingAccount[]>('/existing-accounts');
            return result.success ? { accounts: result.data ?? [] } : { error: result.error ?? 'FAILED_TO_LIST_FUNPAY_ACCOUNTS' };
        } catch (error) {
            console.error(error);
            return { error: serverError(error, 'FAILED_TO_LIST_FUNPAY_ACCOUNTS') };
        }
    }

    /**
     * Puts the venue on another venue's approved account: no KYC, just a new balance account and
     * store. Safe to retry; finished steps are skipped.
     */
    async linkExisting(clientId: string, data: IFunpayLinkExistingData): Promise<{ error?: string }> {
        try {
            const result = await this.api.funpay(clientId).post('/link-existing', data);
            return result.success ? {} : { error: result.error ?? 'FAILED_TO_LINK_FUNPAY_ACCOUNT' };
        } catch (error) {
            console.error(error);
            return { error: serverError(error, 'FAILED_TO_LINK_FUNPAY_ACCOUNT') };
        }
    }

    /**
     * A fresh hosted onboarding link for an account that already exists. Adyen's links are
     * short lived and the signatory usually needs several sittings to finish.
     */
    async createOnboardingLink(clientId: string): Promise<{ onboardingLink?: string; error?: string }> {
        if (!clientId) {
            return { error: 'ONBOARDING_LINK_FAILED_DUE_TO_MISSING_ID' };
        }

        try {
            let result = await this.api.funpay(clientId).post<string>('/onboarding-link', {});

            if (result.success && result.data) {
                return { onboardingLink: result.data };
            }

            return { error: result.error ?? 'FAILED_TO_CREATE_ONBOARDING_LINK' };
        } catch (error) {
            console.error(error);

            return { error: 'FAILED_TO_CREATE_ONBOARDING_LINK' };
        }
    }

    /** Adyen's verdict and the live balances on each of the venue's balance accounts. */
    async getDashboard(clientId: string): Promise<{ dashboard?: IFunpayDashboard; error?: string }> {
        if (!clientId) {
            return { error: 'FAILED_TO_READ_FUNPAY_DASHBOARD' };
        }

        try {
            let result = await this.api.funpay(clientId).get<IFunpayDashboard>('/dashboard');

            if (result.success && result.data) {
                return { dashboard: result.data };
            }

            return { error: result.error ?? 'FAILED_TO_READ_FUNPAY_DASHBOARD' };
        } catch (error) {
            console.error(error);

            return { error: (error as any)?.error?.error ?? 'FAILED_TO_READ_FUNPAY_DASHBOARD' };
        }
    }

    /** One page of transactions; pass the returned `nextCursor` back for the next. */
    async getTransactions(
        clientId: string,
        query: IFunpayTransactionQuery = {}
    ): Promise<{ page?: IFunpayTransactionPage; error?: string }> {
        if (!clientId) {
            return { error: 'FAILED_TO_LIST_FUNPAY_TRANSACTIONS' };
        }

        const params = new URLSearchParams();
        if (query.balanceAccountId) {
            params.set('balanceAccountId', query.balanceAccountId);
        }
        if (query.from) {
            params.set('from', query.from.toISOString());
        }
        if (query.to) {
            params.set('to', query.to.toISOString());
        }
        if (query.cursor) {
            params.set('cursor', query.cursor);
        }

        try {
            let result = await this.api.funpay(clientId).get<IFunpayTransactionPage>(`/transactions?${params.toString()}`);

            if (result.success && result.data) {
                return { page: result.data };
            }

            return { error: result.error ?? 'FAILED_TO_LIST_FUNPAY_TRANSACTIONS' };
        } catch (error) {
            console.error(error);

            return { error: (error as any)?.error?.error ?? 'FAILED_TO_LIST_FUNPAY_TRANSACTIONS' };
        }
    }

    /**
     * Superadmin only. Reruns the account holder sync into the venue settings from the
     * stored account holder, for when that record had to be repaired by hand.
     */
    async syncAccountHolder(clientId: string): Promise<{ error?: string }> {
        if (!clientId) {
            return { error: 'FAILED_TO_SYNC_FUNPAY_ACCOUNT_HOLDER' };
        }

        try {
            let result = await this.api.funpay(clientId).post<null>('/sync-account-holder', {});

            if (result.success) {
                return {};
            }

            return { error: result.error ?? 'FAILED_TO_SYNC_FUNPAY_ACCOUNT_HOLDER' };
        } catch (error) {
            console.error(error);

            return { error: 'FAILED_TO_SYNC_FUNPAY_ACCOUNT_HOLDER' };
        }
    }
}
